import { Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';

import type { BusinessRelationshipDetail } from '@/entities/business-relationship/api/businessRelationship.api';
import {
  useBusinessRelationshipDetail,
  useBusinessRelationshipList,
} from '@/entities/business-relationship/hooks/useBusinessRelationships';
import {
  getRelationshipGroup,
  getStatusBadgeStyle,
  type RelationshipGroup,
} from '@/entities/business-relationship/lib/statusView';
import { getUploadRegistrationYearMonth } from '@/entities/prescription/lib/uploadSettlementMonth';
import { ErrorState } from '@/shared/components/StateViews';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Icon } from '@/shared/components/ui/icon';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { useUIStore } from '@/stores/uiStore';

import { copyWithToast } from '../utils';
import {
  AddressDialog,
  BusinessLicenseDialog,
  PharmacyDialog,
} from './ClinicHeaderOverlays';
import { MissedDrugsDrawer } from './MissedDrugsDrawer';
import { PrescriptionHistorySection } from './PrescriptionHistorySection';
import { TradeInfoDrawer } from './TradeInfoDrawer';
import { TradeInfoSection } from './TradeInfoSection';

type HeaderOverlay = 'address' | 'license' | 'pharmacy' | null;

/** 헤더 정보 한 칸 — 이름(grey-500) + 값(검정), 14 Medium (Figma info 485:4674) */
const InfoItem = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="flex min-w-0 items-center gap-2 text-body-14-m">
    <span className="shrink-0 text-fg-tertiary">{label}</span>
    {children}
  </div>
);

/**
 * 상세 헤더 (Figma 정보 495:6391) — 종별 배지 · 병의원명 · 요양기관번호 · 전화번호 · 주소 · [문전약국] [사업자등록증].
 * 전화번호는 눌러 복사(토스트), 주소는 눌러 주소 팝업. 값이 없으면 누를 수 없는 글자로 둔다 (M-21).
 */
const ClinicHeader = ({
  detail,
  onReload,
}: {
  detail: BusinessRelationshipDetail;
  onReload: () => void;
}) => {
  const [overlay, setOverlay] = useState<HeaderOverlay>(null);
  const hospital = detail.tradeUnit === 'DRUG';
  const ongoing = getRelationshipGroup(detail.statusView) === 'ongoing';
  const toggle = (key: Exclude<HeaderOverlay, null>) => (open: boolean) =>
    setOverlay(open ? key : null);

  return (
    <section className="flex flex-col gap-4 px-6">
      <div className="flex flex-col items-start gap-2">
        <div className="flex items-center gap-2">
          <Badge state="default" emphasis="weak">
            {detail.clinicTypeLabel || (hospital ? '병원급' : '의원급')}
          </Badge>
          {/* 거래 중이 아닌 거래처는 상태를 함께 보인다 (Figma에는 거래 중 화면만 있다) */}
          {!ongoing && (
            <Badge {...getStatusBadgeStyle(detail.statusView)}>
              {detail.statusView}
            </Badge>
          )}
        </div>
        <h2 className="text-heading-24-b break-keep text-fg-primary">
          {detail.clinicName}
        </h2>
      </div>
      <div className="flex flex-wrap items-start gap-x-10 gap-y-2">
        <InfoItem label="요양기관번호">
          <span className="text-fg-primary">{detail.ykiho ?? '—'}</span>
        </InfoItem>
        <InfoItem label="전화번호">
          {detail.phoneNumber ? (
            // 데스크톱에서는 tel:이 열리지 않는 환경이 많아 번호를 복사한다
            <button
              type="button"
              className="rounded-4 text-fg-primary outline-none hover:underline focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)]"
              title="번호 복사"
              onClick={() =>
                void copyWithToast(
                  detail.phoneNumber!,
                  '번호가 복사되었어요.',
                  '번호를 복사하지 못했어요.',
                )
              }
            >
              {detail.phoneNumber}
            </button>
          ) : (
            <span className="text-fg-light">등록된 전화번호가 없어요</span>
          )}
        </InfoItem>
        <InfoItem label="주소">
          {detail.address ? (
            <button
              type="button"
              className="min-w-0 rounded-4 text-left break-keep text-fg-primary underline underline-offset-2 outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)]"
              onClick={() => setOverlay('address')}
            >
              {detail.address}
            </button>
          ) : (
            <span className="text-fg-light">등록된 주소가 없어요</span>
          )}
        </InfoItem>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Button
          variant="outline"
          leftIcon={<Icon name="pharmacy-cross-solid-24" />}
          onClick={() => setOverlay('pharmacy')}
        >
          문전약국
        </Button>
        <Button
          variant="outline"
          leftIcon={<Icon name="page-solid-24" />}
          disabled={!detail.businessLicenseImageUrl}
          title={
            detail.businessLicenseImageUrl
              ? undefined
              : '등록된 사업자등록증이 없어요'
          }
          onClick={() => setOverlay('license')}
        >
          사업자등록증
        </Button>
      </div>

      <PharmacyDialog
        open={overlay === 'pharmacy'}
        onOpenChange={toggle('pharmacy')}
        pharmacies={detail.pharmacies}
      />
      {detail.address && (
        <AddressDialog
          open={overlay === 'address'}
          onOpenChange={toggle('address')}
          clinicName={detail.clinicName}
          address={detail.address}
        />
      )}
      {detail.businessLicenseImageUrl && (
        <BusinessLicenseDialog
          open={overlay === 'license'}
          onOpenChange={toggle('license')}
          clinicName={detail.clinicName}
          imageUrl={detail.businessLicenseImageUrl}
          onReload={onReload}
        />
      )}
    </section>
  );
};

/**
 * M-04 · M-05 우측 상세 카드 (Figma container/right 485:4665) — 헤더 / 구분선 / 거래 정보 / 처방 내역.
 * 의원급과 병원급은 같은 컴포넌트에서 단위 문구만 바꾼다.
 * v1.0: 라임 등록 배너를 없애고 처방 내역 제목 줄 [처방 등록]이 유일한 쓰기 진입점이다.
 */
export const AccountDetail = ({
  relationshipId,
}: {
  relationshipId: number;
}) => {
  const { data, isLoading, isError, refetch } =
    useBusinessRelationshipDetail(relationshipId);
  // 연도 선택 범위 · 종료 거래처 기본 연도 — 목록 카드 값을 쓴다 (목록 캐시 공유)
  const listItem = useBusinessRelationshipList().data?.items.find(
    (i) => i.relationshipId === relationshipId,
  );
  const openUpload = useUIStore((s) => s.openPrescriptionUpload);
  // 오늘 올리면 들어갈 등록월. 서버도 같은 10일 마감 규칙으로 정한다
  const uploadRegistrationYm = useMemo(
    () => getUploadRegistrationYearMonth(),
    [],
  );
  /** M-19 · M-20 거래 정보 드로어가 열 탭. null이면 닫힘 */
  const [tradeTab, setTradeTab] = useState<RelationshipGroup | null>(null);
  const [missedOpen, setMissedOpen] = useState(false);
  const isHospital = data?.tradeUnit === 'DRUG';

  return (
    <div className="flex flex-col gap-4">
      <Link
        to={ROUTE_PATHS.ACCOUNTS}
        className="flex w-fit items-center gap-1 text-body-13-r text-fg-tertiary lg:hidden"
      >
        <Icon name="nav-arrow-left-line-16" />
        목록
      </Link>
      <div className="flex flex-col gap-8 rounded-20 bg-surface-white py-6">
        {isLoading && (
          <div className="flex flex-col gap-4 px-6">
            <Skeleton className="h-6 w-14 rounded-8" />
            <Skeleton className="h-[30px] w-64 rounded-8" />
            <Skeleton className="h-[22px] w-full rounded-8" />
            <Skeleton className="h-10 w-64 rounded-12" />
            <Skeleton className="mt-8 h-[170px] rounded-20" />
            <Skeleton className="h-[228px] rounded-20" />
          </div>
        )}
        {isError && <ErrorState onRetry={() => refetch()} />}
        {data && (
          <>
            <ClinicHeader detail={data} onReload={() => void refetch()} />
            <hr className="border-line-grey" />
            <TradeInfoSection
              isHospital={isHospital}
              counts={data.tradeStatusCounts}
              untradedApplicableCount={data.untradedApplicableCount}
              onOpenTrade={setTradeTab}
              onOpenMissed={() => setMissedOpen(true)}
            />
            <PrescriptionHistorySection
              key={data.relationshipId}
              relationshipId={data.relationshipId}
              clinicId={data.clinicId}
              currentRegistrationYm={uploadRegistrationYm}
              latestApprovalDate={listItem?.latestApprovalDate ?? null}
              endedAt={
                getRelationshipGroup(data.statusView) === 'closed'
                  ? data.endedAt
                  : null
              }
              highlightCurrent={
                getRelationshipGroup(data.statusView) === 'ongoing'
              }
              // 거래 신청 · 종료 · 거절은 호스트가 "등록할 수 없어요" 팝업을 띄운다 (M-07 §4)
              onRegister={() =>
                openUpload({
                  relationshipId: data.relationshipId,
                  clinicName: data.clinicName,
                  statusView: data.statusView,
                })
              }
            />
            <TradeInfoDrawer
              detail={data}
              isHospital={isHospital}
              tab={tradeTab}
              onTabChange={setTradeTab}
              onClose={() => setTradeTab(null)}
            />
            <MissedDrugsDrawer
              detail={data}
              open={missedOpen}
              onClose={() => setMissedOpen(false)}
            />
          </>
        )}
      </div>
    </div>
  );
};
